import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import DemoFeedbackModal from './DemoFeedbackModal';
import PaidFeedbackModal from './PaidFeedbackModal';
import './Pending.css';

const API = process.env.REACT_APP_API_BASE_URL;

const initialsOf = (first = '', last = '') => `${first[0] || ''}${last[0] || ''}`.toUpperCase() || 'S';

const ageLabel = (date) => {
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const Pending = ({ userData, onSubmitrefresh }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const teacherId = userData?.id;

  // ---------- Load pending feedback (same endpoint) ----------
  const load = useCallback(async () => {
    if (!teacherId) return;
    try {
      const res = await axios.get(`${API}/api/teacher/teacherfeedback/${teacherId}`);
      const list = (res.data?.pendingFeedbacks || [])
        .map((f) => ({ ...f, date: new Date(f.ClassDateTime) }))
        // oldest first: the most overdue feedback is on top
        .sort((a, b) => a.date - b.date);
      setFeedbacks(list);
      setError('');
    } catch (err) {
      setError('We couldn’t load your pending feedback.');
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const openFeedback = (f) => {
    // same fields as before for the feedback modals
    setSelected({ ...f, IdSchedule: f.Id, IdTeacher: teacherId, IdStudent: f.IdUser });
  };

  const handleSubmitSuccess = async () => {
    await load();
    if (typeof onSubmitrefresh === 'function') onSubmitrefresh(); // updates the tab badge
  };

  if (loading) {
    return (
      <div className="rmpf" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rmpf-skel" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmpf">
        <div className="rmpf-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmpf-btn" onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <div className="rmpf">
        <div className="rmpf-empty">
          <FontAwesomeIcon icon={faCircleCheck} className="is-done" />
          <p>All caught up! No feedback waiting.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rmpf">
      <p className="rmpf-intro">
        <strong>{feedbacks.length}</strong> {feedbacks.length === 1 ? 'class needs' : 'classes need'} your feedback. The oldest is on top.
      </p>

      <ul className="rmpf-list">
        {feedbacks.map((f, i) => {
          const isPaid = f.Type === 0;
          const name = [f.FirstName, f.LastName].filter(Boolean).join(' ') || 'Student';
          const valid = !Number.isNaN(f.date.getTime());
          const age = valid ? ageLabel(f.date) : '';
          const late = valid && Date.now() - f.date.getTime() > 2 * 86400000;
          return (
            <li key={f.Id ?? i} className={`rmpf-card is-${isPaid ? 'paid' : 'demo'}`}>
              <div className="rmpf-main">
                <span className="rmpf-avatar">
                  {f.Image ? <img src={f.Image} alt="" /> : <span aria-hidden="true">{initialsOf(f.FirstName, f.LastName)}</span>}
                </span>
                <div className="rmpf-who">
                  <h3>{name}</h3>
                  <div className="rmpf-tags">
                    <span className={`rmpf-tag is-${isPaid ? 'paid' : 'demo'}`}>{isPaid ? 'Paid' : 'Trial'}</span>
                    {age && <span className={`rmpf-tag ${late ? 'is-late' : ''}`}>{age}</span>}
                  </div>
                </div>
              </div>

              <div className="rmpf-side">
                {valid && (
                  <div className="rmpf-when">
                    <span>{f.date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    <strong>{f.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                  </div>
                )}
                <button type="button" className="rmpf-btn" onClick={() => openFeedback(f)}>
                  <FontAwesomeIcon icon={faPen} /> Give feedback
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {selected && selected.Type === 1 && (
        <DemoFeedbackModal feedback={selected} onClose={() => setSelected(null)} onSubmitSuccess={handleSubmitSuccess} />
      )}
      {selected && selected.Type === 0 && (
        <PaidFeedbackModal feedback={selected} onClose={() => setSelected(null)} onSubmitSuccess={handleSubmitSuccess} />
      )}
    </div>
  );
};

export default Pending;